<template>
  <v-form
    @submit.prevent="handleSignup"
    ref="form"
    class="signupForm pa-12 ma-auto text-center">
    <!-- Header -->
    <div class="signupForm-header mb-12">
      <div class="signupForm-header-logo">
        <img src="@/assets/app-logo.png" alt="Mank Logo">
      </div>
      <div class="signupForm-header-title">
        <h2 class="text-h5 text-bold">
          Create your account
        </h2>
        <h2 class="text-h7 text--secondary">
          Join us to start your journey
        </h2>
      </div>
    </div>

    <!-- Body -->
    <div class="signupForm-body signupForm-body--align mb-12">
      <!-- First Name + Last Name aynı satırda -->
      <div class="signupForm-body-input signupForm-name-row mb-6">
        <div class="d-flex">
          <div class="signupForm-name-col flex-1">
            <div class="text-element-2 text-semibold text-left mb-4">
              First Name
            </div>
            <MankTextField
              variant="solo"
              v-model="firstName"
              placeholder="Enter your first name"
              :rules="requiredRules"/>
          </div>
          <div class="signupForm-name-col flex-1">
            <div class="text-element-2 text-semibold text-left mb-4">
              Last Name
            </div>
            <MankTextField
              variant="solo"
              v-model="lastName"
              placeholder="Enter your last name"
              :rules="requiredRules"/>
          </div>
        </div>
      </div>

      <!-- Email -->
      <div class="signupForm-body-input mb-6">
        <div class="text-element-2 text-semibold text-left mb-4">
          Email
        </div>
        <MankTextField
          variant="solo"
          v-model="email"
          placeholder="Enter your email"
          :rules="emailRules"
          type="email"
          required/>
      </div>

      <!-- Password -->
      <div class="signupForm-body-input mb-6">
        <div class="text-element-2 text-semibold text-left mb-4">
          Password
        </div>
        <MankTextField
          variant="solo"
          v-model="password"
          placeholder="Create a password"
          :rules="passwordRules"
          :type="showPassword ? 'text' : 'password'"
          @click:append="showPassword = !showPassword"
          required/>
      </div>

      <!-- Confirm Password -->
      <div class="signupForm-body-input">
        <div class="text-element-2 text-semibold text-left mb-4">
          Confirm Password
        </div>
        <MankTextField
          variant="solo"
          v-model="confirmPassword"
          placeholder="Confirm your password"
          :rules="confirmPasswordRules"
          :type="showPassword ? 'text' : 'password'"
          required/>
      </div>
    </div>

    <!-- Signup Button -->
    <v-btn
      type="submit"
      color="primary"
      block
      :loading="loading"
      :disabled="!valid"
      elevation="0"
      class="mt-6">
      Sign Up
    </v-btn>

    <!-- Footer -->
    <div class="mt-6 text-element-2">
      Already have an account?
      <span
        class="text-primary cursor-pointer text-semibold"
        @click="$router.push('/login')">
        Login
      </span>
    </div>
  </v-form>
</template>

<script>
export default {
  name: 'Signup',
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      showPassword: false,
      loading: false,
      requiredRules: [
        (v) => !!v || 'This field is required',
      ],
      emailRules: [
        (v) => !!v || 'This field is required',
        (v) => /.+@.+\..+/.test(v) || 'Please enter a valid email address',
      ],
      passwordRules: [
        (v) => !!v || 'This field is required',
        (v) => v.length >= 6 || 'Password must be at least 6 characters',
      ],
    };
  },
  computed: {
    confirmPasswordRules() {
      return [
        (v) => !!v || 'This field is required',
        (v) => v === this.password || 'Passwords do not match',
      ];
    },
    valid() {
      const firstNameValid = this.firstName
        && this.requiredRules.every((rule) => rule(this.firstName) === true);

      const lastNameValid = this.lastName
        && this.requiredRules.every((rule) => rule(this.lastName) === true);

      const emailValid = this.email
        && this.emailRules.every((rule) => rule(this.email) === true);

      const passwordValid = this.password
        && this.passwordRules.every((rule) => rule(this.password) === true);

      const confirmValid = this.confirmPassword
        && this.confirmPasswordRules.every(
          (rule) => rule(this.confirmPassword) === true,
        );

      return (
        firstNameValid
        && lastNameValid
        && emailValid
        && passwordValid
        && confirmValid
      );
    },
  },
  methods: {
    async handleSignup() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        try {
          await this.$store.dispatch('auth/signUp', {
            given_name: this.firstName,
            family_name: this.lastName,
            email: this.email,
            password: this.password,
          });

          // Signup başarılı → Login ekranına yönlendir
          this.$router.push('/login');
        } finally {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.signupForm {
  border-radius: 16px;
  width: 420px;
  background: $light-surface-2;
}
.signupForm-body--align {
  text-align: left;
}

.cursor-pointer {
  cursor: pointer;
}

/* Auth formu beyaz zemin üzerinde; mank-input'a çerçeve ver ki belli olsun */
.signupForm ::v-deep .mank-input .v-input__slot {
  border: 1px solid $light-surface-3;
  border-radius: 8px;
}
.signupForm ::v-deep .mank-input.v-input--is-focused .v-input__slot {
  border-color: $light-surface-4;
}

/* First/Last Name aynı satırda, Email ile hizalı – kayma olmasın */
.signupForm-name-row {
  width: 100%;
  box-sizing: border-box;
}
.signupForm-name-row .d-flex {
  width: 100%;
  margin: 0;
  box-sizing: border-box;
  gap: 12px; /* iki input arasında boşluk */
}
.signupForm-name-col {
  min-width: 0;
  flex: 1 1 0; /* eşit genişlik, gap dışında taşma yok */
  box-sizing: border-box;
}
/* Tüm input satırları aynı hizada; Vuetify margin kaymasını kaldır */
.signupForm-body-input ::v-deep .v-input {
  margin-bottom: 0;
}
.signupForm-body-input ::v-deep .v-input__control {
  margin: 0;
}
</style>
